<script setup lang="ts">
  import { Icon, IconName, IconSize, Input, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import type { DepartureCity } from '../model/types';
  import { useDepartureCity } from '../model/useDepartureCity';
  import DepartureCityDropdown from './DepartureCityDropdown.vue';
  import { DEPARTURE_CITY_TEXTS } from '../config/texts';

  interface DepartureCityFieldProps {
    modelValue?: DepartureCity | null;
    cities?: DepartureCity[];
  }

  const props = withDefaults(defineProps<DepartureCityFieldProps>(), {
    modelValue: null,
    cities: () => [],
  });

  interface DepartureCityFieldEmits {
    (e: 'update:modelValue', city: DepartureCity | null): void;
    (e: 'change', city: DepartureCity | null): void;
  }

  const emit = defineEmits<DepartureCityFieldEmits>();

  const dest = useDepartureCity({ initialCities: props.cities ?? [] });

  watch(
    () => props.cities,
    (list) => dest.setCities(list ?? []),
  );

  const inputRef = ref<{ focus: () => void } | null>(null);

  const focusInput = (): void => {
    inputRef.value?.focus();
  };

  const onOpen = (): void => {
    dest.open();
  };

  const onSelect = (city: DepartureCity): void => {
    dest.selectCity(city);
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      dest.close();
    }
  };

  const onClear = (): void => {
    dest.clear();
  };

  const rootRef = ref<HTMLElement | null>(null);

  const onOutsidePointer = (event: MouseEvent): void => {
    const root = rootRef.value;
    const target = event.target as Node | null;
    if (root && target && !root.contains(target)) {
      dest.close();
    }
  };

  watch(dest.controlActivated, (active) => {
    if (active) {
      document.addEventListener('mousedown', onOutsidePointer);
    } else {
      document.removeEventListener('mousedown', onOutsidePointer);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onOutsidePointer);
  });

  watch(dest.selection, (city) => {
    emit('update:modelValue', city);
    emit('change', city);
  });

  onMounted(() => {
    if (props.modelValue) {
      dest.restore(props.modelValue);
    }
  });

  defineExpose({
    validate: (): boolean => dest.validate(),
    clear: (): void => dest.clear(),
  });
</script>

<template>
  <div ref="rootRef" class="departure-city-field" data-testid="departure-city-field">
    <DepartureCityDropdown
      :options="dest.optionsList.value"
      :selected-id="dest.selection.value?.id ?? null"
      :visible="dest.controlActivated.value"
      @select="onSelect"
      @close="dest.close"
    >
      <template #reference>
        <div
          class="departure-city-field__control"
          :class="{ 'departure-city-field__control_active': dest.controlActivated.value }"
          @click="focusInput"
        >
          <Typo
            class="departure-city-field__label"
            :variant="TypoVariant.BodySRegular"
            tag="span"
          >{{ DEPARTURE_CITY_TEXTS.fieldLabelFixed }}</Typo>

          <div class="departure-city-field__value">
            <Input
              ref="inputRef"
              :model-value="dest.inputString.value"
              placeholder=""
              :error="dest.invalid.value"
              :bg="false"
              :bordered="false"
              size="xs"
              name="input"
              data-testid="departure-city-input"
              @update:modelValue="dest.setInput"
              @focus="onOpen"
              @click="onOpen"
              @keydown.native="onKeydown"
              class="departure-city-field__input"
            >
              <template #append>
                <button
                  v-if="dest.clearVisible.value"
                  type="button"
                  class="departure-city-field__clear"
                  data-testid="departure-city-clear"
                  aria-label="Очистить"
                  @click.stop.prevent="onClear"
                >
                  <Icon :name="IconName.Close" :size="IconSize.S" />
                </button>
              </template>
            </Input>
          </div>
        </div>
      </template>
    </DepartureCityDropdown>

    <div
      v-if="dest.errorText.value"
      class="departure-city-field__error"
      data-testid="departure-city-error"
    >
      <Typo :variant="TypoVariant.BodySRegular" tag="span">{{ dest.errorText.value }}</Typo>
    </div>
  </div>
</template>

<style scoped lang="scss">
  :deep(.departure-city-field__input input[name="input"]) {
    font-size: 16px;
  }
  .departure-city-field {
    position: relative;
    width: 100%;

    &__control {
      background: var(--color-bg-primary);
      cursor: text;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      padding: var(--spacing-xs) var(--spacing-s);
      transition: background-color var(--transition-default);

      &_active {
        background: var(--color-disabled-100);
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }

    &__value {
      :deep(.tl-input) {
        padding: 0;
      }
    }

    &__clear {
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      display: inline-flex;
      justify-content: center;
      padding: 0;
      color: var(--color-text-secondary);
    }

    &__error {
      color: var(--color-error-500);
      margin-top: var(--spacing-2xs);
    }
  }
</style>
